/**
 * TemplateInlineIndicators
 * ------------------------
 * TREATX-11C-ADMIN-TEMPLATE-EDITOR-INLINE-INDICATORS-01 (2026-06-15)
 *
 * Per-template inline status badges for the canonical Admin Template
 * editor pages. Indicators surfaced (all read-only, all derived from
 * data the operator already has access to):
 *
 *   1. Active / Inactive             — from `isActive`
 *   2. Origin: DEFAULT / EDITED      — from `template-status` API
 *                                      (`is_default`)
 *   3. Inline fallback warning       — when this event_key is NOT
 *                                      yet seeded into `email_templates`
 *                                      and the renderer will fall back
 *                                      to inline copy
 *   4. Missing variables             — client-side scan of {{var}}
 *                                      placeholders vs. the event's
 *                                      `defaultVariables` allow-list
 *   5. Last updated                  — from `template-status`
 *                                      (`last_modified`)
 *   6. Last delivery status          — from `template-status`
 *                                      (`last_delivery.status`)
 *   7. Test-send availability        — present whenever the
 *                                      `/email_templates/test-send`
 *                                      endpoint is reachable (always
 *                                      true in this build)
 *
 * No new endpoint, no new SSOT. Consumes
 *   GET /api/admin/notifications/template-status
 * via the existing `useTemplateStatusMap` hook.
 */
import React, { useMemo } from 'react';
import { formatDateTimeLocale as _fmtDateTime } from '../../utils/dateFormat';
import {
  CheckCircle2, AlertTriangle, Clock, FileText, Send, Inbox,
} from 'lucide-react';

function formatDateLocale(iso) {
  if (!iso) return null;
  try {
    return _fmtDateTime(iso);
  } catch {
    return iso;
  }
}

function extractPlaceholders(text) {
  if (!text) return [];
  const out = [];
  const re = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
  let m;
  while ((m = re.exec(text)) !== null) out.push(m[1]);
  return Array.from(new Set(out));
}

function statusBadgeClasses(status) {
  const v = (status || '').toLowerCase();
  if (v.includes('sent')) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (v.includes('fail')) return 'bg-rose-100 text-rose-700 border-rose-200';
  if (v.includes('block')) return 'bg-amber-100 text-amber-700 border-amber-200';
  if (v.includes('queue')) return 'bg-sky-100 text-sky-700 border-sky-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
}

const Pill = ({ className = '', children, testid }) => (
  <span
    className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-full border ${className}`}
    data-testid={testid}
  >
    {children}
  </span>
);

const TemplateInlineIndicators = ({
  eventKey,
  isActive = true,
  subject = '',
  body = '',
  allowedVariables = null, // optional array of allowed placeholder keys
  serverStatus = null,     // row from /admin/notifications/template-status
}) => {
  const placeholders = useMemo(
    () => extractPlaceholders(`${subject || ''}\n${body || ''}`),
    [subject, body]
  );

  const missingVars = useMemo(() => {
    if (!Array.isArray(allowedVariables)) return [];
    const allowSet = new Set(allowedVariables);
    return placeholders.filter((p) => !allowSet.has(p));
  }, [placeholders, allowedVariables]);

  const usingInlineFallback = !serverStatus; // event_key absent from email_templates
  const isDefault = serverStatus ? !!serverStatus.is_default : true;
  const lastModified = serverStatus ? formatDateLocale(serverStatus.last_modified) : null;
  const lastDelivery = serverStatus ? serverStatus.last_delivery : null;

  return (
    <div
      className="flex flex-wrap items-center gap-1.5 mt-1"
      data-testid={`template-inline-indicators-${eventKey || 'unknown'}`}
    >
      {/* 1. Active / Inactive */}
      {isActive ? (
        <Pill
          className="bg-emerald-50 text-emerald-700 border-emerald-200"
          testid={`indicator-active-${eventKey}`}
        >
          <CheckCircle2 className="h-3 w-3" /> Active
        </Pill>
      ) : (
        <Pill
          className="bg-slate-100 text-slate-500 border-slate-200"
          testid={`indicator-inactive-${eventKey}`}
        >
          <Inbox className="h-3 w-3" /> Inactive
        </Pill>
      )}

      {/* 2. Origin */}
      {isDefault ? (
        <Pill
          className="bg-slate-100 text-slate-600 border-slate-200"
          testid={`indicator-origin-default-${eventKey}`}
        >
          <FileText className="h-3 w-3" /> Default
        </Pill>
      ) : (
        <Pill
          className="bg-emerald-50 text-emerald-700 border-emerald-200"
          testid={`indicator-origin-edited-${eventKey}`}
        >
          <FileText className="h-3 w-3" /> Edited
        </Pill>
      )}

      {/* 3. Inline-fallback warning (event_key not yet seeded server-side) */}
      {usingInlineFallback && (
        <Pill
          className="bg-amber-50 text-amber-700 border-amber-200"
          testid={`indicator-inline-fallback-${eventKey}`}
          title="This event is not yet persisted in email_templates. Renderer will use inline copy until edited & saved."
        >
          <AlertTriangle className="h-3 w-3" /> Inline fallback
        </Pill>
      )}

      {/* 4. Missing-variables warning */}
      {missingVars.length > 0 && (
        <Pill
          className="bg-amber-50 text-amber-700 border-amber-200"
          testid={`indicator-missing-vars-${eventKey}`}
          title={`Unknown placeholders: ${missingVars.join(', ')}`}
        >
          <AlertTriangle className="h-3 w-3" />
          {missingVars.length} unknown var{missingVars.length === 1 ? '' : 's'}
        </Pill>
      )}

      {/* 5. Last updated */}
      {lastModified && (
        <Pill
          className="bg-slate-50 text-slate-600 border-slate-200"
          testid={`indicator-last-modified-${eventKey}`}
        >
          <Clock className="h-3 w-3" /> Updated {lastModified}
        </Pill>
      )}

      {/* 6. Last delivery */}
      {lastDelivery && lastDelivery.status && (
        <Pill
          className={statusBadgeClasses(lastDelivery.status)}
          testid={`indicator-last-delivery-${eventKey}`}
          title={`Last delivery: ${lastDelivery.status} @ ${formatDateLocale(lastDelivery.created_at) || 'unknown'}`}
        >
          <Send className="h-3 w-3" /> {String(lastDelivery.status).toLowerCase()}
        </Pill>
      )}

      {/* 7. Test-send availability */}
      <Pill
        className="bg-sky-50 text-sky-700 border-sky-200"
        testid={`indicator-test-send-ready-${eventKey}`}
        title="Test send is available via the existing /email_templates/test-send endpoint."
      >
        <Send className="h-3 w-3" /> Test-send ready
      </Pill>
    </div>
  );
};

export default TemplateInlineIndicators;
